import React from 'react';
import { Link, useParams, useNavigate } from 'react-router';
import usePageMeta from './public/usePageMeta';

// ── Model/variant benchmark definitions ──────────────────────────────────────
// Metrics sourced from published papers, official repos, and benchmark JSONs.
// Each source URL is cited per-variant for transparency.

// Availability of a variant inside Spectra, which is a separate question from
// whether the upstream model has published benchmarks. A paper number can be
// real while the variant is not something a Spectra run can reach.
export const AVAILABILITY = {
  experimental: { label: 'Experimental', color: '#f59e0b', bg: '#422006' },
  unavailable: { label: 'Not available in Spectra', color: '#94a3b8', bg: '#1e293b' },
};

export const BENCHMARK_MODELS = [
  {
    id: 'mattergen',
    name: 'MatterGen',
    category: 'Crystal Generation',
    description: 'Diffusion-based inorganic crystal generator conditioned on target properties. Trained on 608,000 stable materials from Materials Project + Alexandria databases.',
    paper: 'https://www.nature.com/articles/s41586-025-08628-5',
    paperLabel: 'Nature (2025)',
    repo: 'https://github.com/microsoft/mattergen',
    variants: [
      {
        id: 'mattergen_base',
        name: 'MatterGen Base (Alex-MP)',
        description: 'Unconditional generation trained on the full Alex-MP dataset (608k stable materials).',
        sources: [
          { label: 'Nature (2025)', url: 'https://www.nature.com/articles/s41586-025-08628-5' },
          { label: 'GitHub benchmark JSON', url: 'https://github.com/microsoft/mattergen' },
        ],
        metrics: [
          { name: 'Structure Validity', value: '100%', description: 'No atom-atom distances < 0.5 Å or volume < 0.1 ų' },
          { name: 'Composition Validity', value: '88.5%', description: 'Valid by SMACT charge neutrality check' },
          { name: 'Uniqueness', value: '99.9%', description: 'Fraction of non-duplicate structures in batch' },
          { name: 'Novelty', value: '61.6%', description: 'Fraction not found in MP2020 training reference' },
          { name: 'Stability Rate', value: '73.3%', description: 'E_above_hull < 0.1 eV/atom (MP2020 convex hull)' },
          { name: 'S.U.N. Rate', value: '38.9%', description: 'Stable + Unique + Novel (the key composite metric)' },
          { name: 'Avg E_above_hull', value: '0.075 eV/atom', description: 'Mean energy above convex hull' },
          { name: 'Avg RMSD', value: '0.063 Å', description: 'Mean displacement from DFT-relaxed geometry' },
        ],
        testSet: 'MP2020correction convex hull reference (Materials Project)',
        notes: 'Primary checkpoint trained on Alex-MP. S.U.N. rate is 2.1x higher than the next best model (DiffCSP at 18.3%). RMSD is 7.6x lower than CDVAE (0.479 Å), indicating structures are much closer to local energy minima.',
        comparison: [
          { model: 'MatterGen (Alex-MP)', sun: '38.9%', stable: '73.3%', novel: '61.6%', unique: '99.9%', rmsd: '0.063 Å', eHull: '0.075' },
          { model: 'MatterGen (MP-20)', sun: '25.8%', stable: '47.1%', novel: '76.0%', unique: '99.8%', rmsd: '0.147 Å', eHull: '0.201' },
          { model: 'DiffCSP (MP-20)', sun: '20.1%', stable: '41.3%', novel: '75.8%', unique: '99.4%', rmsd: '0.413 Å', eHull: '0.189' },
          { model: 'CDVAE', sun: '18.3%', stable: '24.3%', novel: '92.3%', unique: '99.9%', rmsd: '0.479 Å', eHull: '0.243' },
          { model: 'P-G-SchNet', sun: '—', stable: '—', novel: '—', unique: '—', rmsd: '—', eHull: '—' },
        ],
      },
      {
        id: 'mp_20_base',
        name: 'MatterGen (MP-20)',
        description: 'Base checkpoint trained on MP-20 subset (45,231 structures, ≤20 atoms).',
        sources: [
          { label: 'GitHub benchmark JSON', url: 'https://github.com/microsoft/mattergen' },
        ],
        metrics: [
          { name: 'Structure Validity', value: '100%', description: 'No atom-atom distances < 0.5 Å' },
          { name: 'Composition Validity', value: '84.5%', description: 'Valid by SMACT' },
          { name: 'Uniqueness', value: '99.8%', description: 'Non-duplicate structures' },
          { name: 'Novelty', value: '76.0%', description: 'Not in MP2020 reference' },
          { name: 'Stability Rate', value: '47.1%', description: 'E_above_hull < 0.1 eV/atom' },
          { name: 'S.U.N. Rate', value: '25.8%', description: 'Stable + Unique + Novel' },
          { name: 'Avg E_above_hull', value: '0.201 eV/atom', description: 'Mean energy above convex hull' },
          { name: 'Avg RMSD', value: '0.147 Å', description: 'Displacement from DFT relaxation' },
        ],
        testSet: 'MP2020correction (Materials Project, ≤20 atoms)',
        notes: 'Smaller training set than Alex-MP. Higher novelty (76% vs 62%) but lower stability (47% vs 73%) — the larger training set produces more physically grounded structures.',
      },
      {
        id: 'dft_band_gap',
        name: 'Band Gap Conditioning (DFT)',
        description: 'Fine-tuned to generate crystals matching a target DFT band gap value.',
        sources: [
          { label: 'Nature (2025)', url: 'https://www.nature.com/articles/s41586-025-08628-5' },
        ],
        metrics: [
          { name: 'Structure Validity', value: '100%', description: 'Inherited from base architecture' },
          { name: 'Target Conditioning', value: 'Supported', description: 'Accepts band gap target in eV; exact MAE behind Nature paywall' },
        ],
        testSet: 'Materials Project band gap subset',
        notes: 'Property conditioning MAE values are reported in the Nature paper (Figures 3–4) but behind paywall. The paper demonstrates successful generation of semiconductors with targeted band gaps across 0–6 eV range.',
      },
      {
        id: 'ml_bulk_modulus',
        name: 'Bulk Modulus Conditioning (ML)',
        description: 'Fine-tuned to generate crystals matching a target ML-predicted bulk modulus.',
        sources: [
          { label: 'Nature (2025)', url: 'https://www.nature.com/articles/s41586-025-08628-5' },
        ],
        metrics: [
          { name: 'Structure Validity', value: '100%', description: 'Inherited from base architecture' },
          { name: 'Target Conditioning', value: 'Supported', description: 'Accepts bulk modulus target in GPa' },
          { name: 'Experimental Validation', value: 'TaCr\u2082O\u2086', description: 'Designed for 200 GPa, measured 169 GPa (~15% error)' },
        ],
        testSet: 'Materials Project elastic properties subset',
        notes: 'Paper reports 106 S.U.N. structures with >400 GPa bulk modulus from 180 DFT calculations — only 2 training structures had similar values. One candidate (TaCr₂O₆) was experimentally validated.',
      },
      {
        id: 'chemical_system',
        name: 'Chemical System Conditioning',
        description: 'Generates crystals constrained to specified element sets.',
        sources: [
          { label: 'Nature (2025)', url: 'https://www.nature.com/articles/s41586-025-08628-5' },
        ],
        metrics: [
          { name: 'S.U.N. (well-explored)', value: '83%', description: 'Chemical systems with many known phases' },
          { name: 'S.U.N. (partially explored)', value: '65%', description: 'Systems with some known phases' },
          { name: 'S.U.N. (unexplored)', value: '49%', description: 'Systems with no known stable phases' },
        ],
        testSet: 'Materials Project chemical systems',
        notes: 'Performance degrades gracefully from well-explored to unexplored chemical systems. Even for completely unexplored systems, nearly half of generated structures are stable, unique, and novel.',
      },
      {
        id: 'chemical_system_energy_above_hull',
        name: 'Composition + Stability',
        description: 'Chemical system conditioning with energy-above-hull stability target.',
        sources: [
          { label: 'Nature (2025)', url: 'https://www.nature.com/articles/s41586-025-08628-5' },
        ],
        metrics: [
          { name: 'Target Conditioning', value: 'Supported', description: 'Jointly conditions on element set + thermodynamic stability' },
        ],
        testSet: 'Materials Project thermodynamic data',
        notes: 'Combined checkpoint. Detailed MAE values in Nature paper (paywalled).',
      },
      {
        id: 'dft_mag_density',
        name: 'Magnetic Density Conditioning',
        description: 'Generates crystals conditioned on target magnetic density (μB/ų).',
        sources: [
          { label: 'Nature (2025)', url: 'https://www.nature.com/articles/s41586-025-08628-5' },
        ],
        metrics: [
          { name: 'Target Conditioning', value: 'Supported', description: 'Accepts magnetic density target > 0 μB/ų' },
          { name: 'Validation', value: '18 S.U.N.', description: '18 stable/unique/novel magnetic structures from 180 DFT calcs (target > 0.2 μB/ų)' },
        ],
        testSet: 'Materials Project magnetic materials subset',
        notes: 'Generates novel magnetic materials including candidates for permanent magnet applications. 18 S.U.N. structures from 180 DFT calculations at > 0.2 μB/ų target.',
      },
      {
        id: 'space_group',
        name: 'Space Group Conditioning',
        description: 'Generates crystals constrained to a target space group (1–230).',
        sources: [
          { label: 'Nature (2025)', url: 'https://www.nature.com/articles/s41586-025-08628-5' },
        ],
        metrics: [
          { name: 'Coverage', value: 'All 7 lattice types', description: 'Successfully generates across all crystal families' },
          { name: 'SG Tested', value: '14 space groups', description: 'Validated across 14 representative space groups' },
        ],
        testSet: 'Materials Project (all 230 space groups)',
        notes: 'Exact compliance rates are in the Nature paper (paywalled). Covers triclinic through cubic crystal systems.',
      },
    ],
  },
  {
    id: 'fm4m',
    name: 'IBM FM4M (SMI-TED)',
    category: 'Molecular Generation & Property Prediction',
    description: 'IBM Foundation Model for Materials — pre-trained on 91M SMILES from PubChem. BART, MHG, and SMI-TED model variants for molecular generation and featurization. IBM archived the upstream FM4M repository on 2026-10-07, making it read-only with no successor announced; the published model weights remain available under Apache-2.0 and are what Spectra runs, so these capabilities are unaffected.',
    paper: 'https://arxiv.org/abs/2407.20267',
    paperLabel: 'arXiv (2024)',
    repo: 'https://github.com/IBM/materials',
    variants: [
      {
        id: 'smi-ted',
        name: 'SMI-TED 289M',
        description: 'Large encoder-decoder foundation model (289M params) pre-trained on 91M SMILES. Generation + property prediction.',
        sources: [
          { label: 'arXiv 2407.20267', url: 'https://arxiv.org/abs/2407.20267' },
        ],
        metrics: [
          { name: 'Validity (fragment)', value: '99.99%', description: 'Generated SMILES that are chemically valid (MOSES benchmark)' },
          { name: 'Scaffold Novelty', value: '99.99%', description: 'Novel Bemis-Murcko scaffolds' },
          { name: 'Internal Diversity', value: '85.65%', description: 'Pairwise Tanimoto distance within generated set' },
          { name: 'FCD Score', value: '1.153', description: 'Fréchet ChemNet Distance (lower = more realistic)' },
          { name: 'BBBP (ROC-AUC)', value: '92.26%', description: 'Blood-brain barrier permeability classification' },
          { name: 'ClinTox (ROC-AUC)', value: '94.27%', description: 'Clinical toxicity classification' },
          { name: 'BACE (ROC-AUC)', value: '88.24%', description: 'BACE-1 inhibitor classification' },
          { name: 'ESOL (RMSE)', value: '0.611', description: 'Aqueous solubility prediction (log mol/L)' },
          { name: 'Lipophilicity (RMSE)', value: '0.552', description: 'Lipophilicity prediction (logD)' },
          { name: 'FreeSolv (RMSE)', value: '1.223', description: 'Solvation free energy prediction (kcal/mol)' },
        ],
        testSet: 'MOSES benchmark (generation), MoleculeNet (property prediction)',
        notes: 'Pre-trained on 91 million SMILES from PubChem. Evaluated on both MOSES generation benchmarks and MoleculeNet property prediction tasks. Classification results are ROC-AUC (higher is better), regression results are RMSE (lower is better).',
      },
      {
        id: 'bart',
        name: 'BART Generative',
        description: 'De-novo molecule generation via BART autoregressive model.',
        sources: [
          { label: 'IBM Materials GitHub', url: 'https://github.com/IBM/materials' },
        ],
        metrics: [
          { name: 'Status', value: 'Integrated', description: 'No standalone published benchmarks — generation uses FM4M BART architecture' },
        ],
        testSet: 'No published benchmark',
        notes: 'BART variant is used for de-novo SMILES generation. Individual benchmark numbers not published separately from the SMI-TED family paper.',
      },
      {
        id: 'mhg',
        name: 'MHG Generative',
        description: 'Molecular Hypergraph Grammar — structure-aware generation preserving ring systems.',
        sources: [
          { label: 'IBM Materials GitHub', url: 'https://github.com/IBM/materials' },
        ],
        metrics: [
          { name: 'Status', value: 'Integrated', description: 'Hypergraph-based generation preserves molecular substructure' },
        ],
        testSet: 'No published benchmark',
        notes: 'MHG enforces chemical validity through grammar rules. Individual benchmark numbers not published separately.',
      },
    ],
  },
  {
    id: 'gt4sd',
    name: 'GT4SD',
    category: 'Property-Conditioned Molecular Generation',
    description: 'IBM Generative Toolkit for Scientific Discovery — wraps multiple algorithms with harmonized interfaces. Experimental in Spectra: property-conditioned generation through GT4SD is not yet verified in production, and the toolkit\'s GuacaMol, REINVENT and MoLeR algorithms are not wired into Spectra. The benchmark numbers below are the upstream published results, not measurements of a Spectra run.',
    paper: 'https://www.nature.com/articles/s41524-023-01028-1',
    paperLabel: 'npj Computational Materials (2023)',
    repo: 'https://github.com/GT4SD/gt4sd-core',
    variants: [
      {
        id: 'regression_transformer',
        name: 'Regression Transformer',
        availability: 'experimental',
        description: 'Property-conditioned generation — specify a target logP and the model edits a seed molecule toward it, jointly predicting and generating.',
        sources: [
          { label: 'Nature Machine Intelligence (2023)', url: 'https://www.nature.com/articles/s42256-023-00639-z' },
          { label: 'GitHub IBM/regression-transformer', url: 'https://github.com/IBM/regression-transformer' },
        ],
        metrics: [
          { name: 'QED Prediction RMSE', value: '< 0.06', description: 'Drug-likeness prediction error (all RT variants, Table 1)' },
          { name: 'Generation Novelty', value: '> 99.3%', description: 'Novel molecules across all property conditioning settings' },
          { name: 'Property Prediction', value: 'Matches/surpasses', description: 'Matches or surpasses conventional regression models for QED, ESOL, logP' },
        ],
        testSet: 'ZINC + custom property labels, QM9',
        notes: 'A single model both predicts properties and generates molecules conditioned on property targets. The metrics above are from the Nature Machine Intelligence paper, not from Spectra; detailed per-property tables are in that paper (paywalled). Spectra status: experimental, and verified locally rather than in production. The integration loads the IBM crippen_logp checkpoint and returns every molecule with its logP measured by RDKit plus the signed distance from your target, so you can see what a run did rather than taking the target on trust; that behaviour was checked against the answer key the checkpoint ships and a local acceptance sweep, not against a production run. Two limits are worth knowing before planning around it: the model edits a seed molecule rather than building one to order, and a local acceptance sweep measured it saturating near 3.4 logP, so higher targets are accepted but rarely reached; and the IBM model card for this checkpoint lists production-level inference as out of scope. Read the per-molecule measurements rather than assuming the target was met.',
      },
      {
        id: 'hugging_face',
        name: 'HuggingFace Causal LM',
        availability: 'unavailable',
        description: 'Unconditioned de-novo molecular generation via a causal language model fine-tuned on chemical SMILES.',
        sources: [
          { label: 'npj Computational Materials (2023)', url: 'https://www.nature.com/articles/s41524-023-01028-1' },
        ],
        metrics: [
          { name: 'Published benchmarks', value: '—', description: 'Model-dependent: GT4SD wraps many HuggingFace checkpoints and publishes no single number' },
        ],
        testSet: 'Model-dependent (GuacaMol, MOSES)',
        notes: 'Spectra offers no built-in checkpoint for this algorithm. It runs only with customer-supplied weights registered as a custom model, where the benchmarks are whatever that customer checkpoint achieves. GT4SD itself wraps many HuggingFace models, so there is no single published number for the algorithm.',
      },
      {
        id: 'guacamol',
        name: 'GuacaMol',
        availability: 'unavailable',
        description: 'Goal-directed molecular generation benchmark suite.',
        sources: [
          { label: 'npj Computational Materials (2023)', url: 'https://www.nature.com/articles/s41524-023-01028-1' },
        ],
        metrics: [
          { name: 'Published benchmarks', value: '—', description: 'GuacaMol is a benchmark framework rather than a single model' },
        ],
        testSet: 'GuacaMol benchmark suite',
        notes: 'Not wired into Spectra: the GT4SD service registers this algorithm as a stub that returns an error naming a missing dependency. Listed here because the upstream toolkit supports it and it may be enabled later. GuacaMol is a benchmark framework rather than a single model.',
      },
      {
        id: 'reinvent',
        name: 'REINVENT',
        availability: 'unavailable',
        description: 'SMILES-based reinforcement learning for de-novo molecule design.',
        sources: [
          { label: 'npj Computational Materials (2023)', url: 'https://www.nature.com/articles/s41524-023-01028-1' },
        ],
        metrics: [
          { name: 'Published benchmarks', value: '—', description: 'See the original REINVENT papers; GT4SD publishes no separate number' },
        ],
        testSet: 'Model-specific',
        notes: 'Not wired into Spectra: the GT4SD service registers this algorithm as a stub that returns an error naming a missing dependency. REINVENT is an established RL-based generator and its benchmarks are in the original papers; listed here because the upstream toolkit supports it.',
      },
      {
        id: 'moler',
        name: 'MoLeR',
        availability: 'unavailable',
        description: 'Scaffold-based molecular generation using graph neural networks.',
        sources: [
          { label: 'npj Computational Materials (2023)', url: 'https://www.nature.com/articles/s41524-023-01028-1' },
        ],
        metrics: [
          { name: 'Tanimoto Similarity', value: '> 0.5', description: 'Generated molecules maintain similarity to scaffold targets' },
          { name: 'ESOL Improvement', value: '> 1 M/L', description: 'Improved aqueous solubility vs target in case study' },
        ],
        testSet: 'GT4SD case study (scaffold optimization)',
        notes: 'Not wired into Spectra: the GT4SD service registers this algorithm as a stub that returns an error naming a missing dependency. The metrics above are from the GT4SD paper case study, in which MoLeR and the Regression Transformer both produced molecules with Tanimoto > 0.5 to target scaffolds and improved ESOL by > 1 M/L.',
      },
    ],
  },
];

// ── Styles ───────────────────────────────────────────────────────────────────
const CARD_BG = '#0f1923';
const INNER_BG = '#0a1120';

// ── Benchmarks Index Page ────────────────────────────────────────────────────
export function BenchmarksIndexPage() {
  usePageMeta({
    title: 'Generative Materials Model Benchmarks: MatterGen, FM4M, GT4SD | Spectra',
    description: 'Published performance metrics for the generative models in Spectra: MatterGen crystal generation, IBM FM4M and GT4SD molecular models, with links to the papers.',
    path: '/docs/benchmarks',
  });
  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <h1 style={{ color: '#e2e8f0', fontSize: '1.8rem', fontWeight: 700, marginBottom: 8 }}>
          Model Benchmarks
        </h1>
        <p style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.7, marginBottom: 32 }}>
          Published prediction accuracy and generation quality metrics for every model and checkpoint variant
          used in the Spectra platform. All numbers are published results, sourced from peer-reviewed
          papers or official repositories, not measurements of a Spectra run. Source links are provided on
          each page, and each variant is labelled with whether Spectra can currently run it.
        </p>

        {BENCHMARK_MODELS.map(model => (
          <div key={model.id} style={{ background: CARD_BG, borderRadius: 12, padding: 24, marginBottom: 20, border: '1px solid #1e293b' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <div>
                <h2 style={{ color: '#e2e8f0', fontSize: '1.2rem', fontWeight: 700, margin: 0 }}>{model.name}</h2>
                <span style={{ color: '#64748b', fontSize: 12 }}>{model.category}</span>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                {model.paper && (
                  <a href={model.paper} target="_blank" rel="noopener noreferrer"
                     style={{ color: '#38bdf8', fontSize: 12, textDecoration: 'none' }}>
                    {model.paperLabel || 'Paper'} →
                  </a>
                )}
                {model.repo && (
                  <a href={model.repo} target="_blank" rel="noopener noreferrer"
                     style={{ color: '#94a3b8', fontSize: 12, textDecoration: 'none' }}>
                    GitHub →
                  </a>
                )}
              </div>
            </div>
            <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>{model.description}</p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
              {model.variants.map(v => (
                <Link key={v.id} to={`/docs/benchmarks/${model.id}/${v.id}`}
                      style={{ textDecoration: 'none' }}>
                  <div style={{
                    background: INNER_BG, borderRadius: 8, padding: '14px 16px',
                    border: '1px solid #1e293b', cursor: 'pointer',
                    transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#38bdf8'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#1e293b'}>
                    <div style={{ color: '#e2e8f0', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{v.name}</div>
                    {v.availability && AVAILABILITY[v.availability] && (
                      <div style={{
                        display: 'inline-block', marginBottom: 6,
                        background: AVAILABILITY[v.availability].bg,
                        color: AVAILABILITY[v.availability].color,
                        borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: 0.4,
                      }}>
                        {AVAILABILITY[v.availability].label}
                      </div>
                    )}
                    <div style={{ color: '#64748b', fontSize: 11, marginBottom: 8 }}>{v.description}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {v.metrics.filter(m => m.value !== 'Integrated' && m.value !== 'Supported').slice(0, 3).map((m, i) => (
                        <span key={i} style={{
                          background: m.value === '—' ? '#1e293b' : '#064e3b',
                          color: m.value === '—' ? '#64748b' : '#4ade80',
                          borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                        }}>
                          {m.name}: {m.value}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}

        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <Link to="/docs" style={{ color: '#38bdf8', fontSize: 13, textDecoration: 'none' }}>← Back to Docs</Link>
        </div>
    </div>
  );
}

// ── Individual Benchmark Page ────────────────────────────────────────────────
export function BenchmarkDetailPage() {
  const { modelId, variantId } = useParams();
  const navigate = useNavigate();

  const model = BENCHMARK_MODELS.find(m => m.id === modelId);
  const variant = model?.variants.find(v => v.id === variantId);
  usePageMeta({
    title: variant ? `${variant.name} Benchmarks | Spectra Model Benchmarks` : 'Benchmark not found | CrimsonScan Spectra',
    description: variant
      ? `Published metrics for ${variant.name} (${model.name}, ${model.category}): ${variant.description}`.slice(0, 300)
      : 'Published performance metrics for the generative models in Spectra.',
    path: `/docs/benchmarks/${modelId}/${variantId}`,
  });

  if (!model || !variant) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 24px' }}>
        <h2 style={{ color: '#f87171', marginBottom: 16 }}>Benchmark not found</h2>
        <Link to="/docs/benchmarks" style={{ color: '#38bdf8', textDecoration: 'none' }}>← Back to Benchmarks</Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
        {/* Breadcrumb */}
        <div style={{ marginBottom: 20, fontSize: 13 }}>
          <Link to="/docs/benchmarks" style={{ color: '#38bdf8', textDecoration: 'none' }}>Benchmarks</Link>
          <span style={{ color: '#475569', margin: '0 8px' }}>/</span>
          <span style={{ color: '#94a3b8' }}>{model.name}</span>
          <span style={{ color: '#475569', margin: '0 8px' }}>/</span>
          <span style={{ color: '#e2e8f0' }}>{variant.name}</span>
        </div>

        {/* Header */}
        <h1 style={{ color: '#e2e8f0', fontSize: '1.5rem', fontWeight: 700, marginBottom: 4 }}>
          {variant.name}
        </h1>
        <div style={{ color: '#64748b', fontSize: 13, marginBottom: 4 }}>{model.name} — {model.category}</div>
        <p style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>{variant.description}</p>

        {/* Sources */}
        {variant.sources?.length > 0 && (
          <div style={{ background: CARD_BG, borderRadius: 12, padding: '16px 24px', marginBottom: 20, border: '1px solid #1e293b' }}>
            <div style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Sources</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {variant.sources.map((s, i) => (
                <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
                   style={{ color: '#38bdf8', fontSize: 13, textDecoration: 'none' }}>
                  {s.label} →
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Metrics table */}
        <div style={{ background: CARD_BG, borderRadius: 12, padding: 24, marginBottom: 20, border: '1px solid #1e293b' }}>
          <h3 style={{ color: '#e2e8f0', fontSize: '1rem', fontWeight: 600, margin: '0 0 16px 0' }}>
            Performance Metrics
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1e293b' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: '#94a3b8', fontSize: 12, fontWeight: 600 }}>Metric</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: '#94a3b8', fontSize: 12, fontWeight: 600 }}>Value</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: '#94a3b8', fontSize: 12, fontWeight: 600 }}>Description</th>
              </tr>
            </thead>
            <tbody>
              {variant.metrics.map((m, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #0f1923' }}>
                  <td style={{ padding: '10px 12px', color: '#e2e8f0', fontSize: 14, fontWeight: 500 }}>{m.name}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <span style={{
                      color: m.value === '—' || m.value === 'Integrated' || m.value === 'Supported' ? '#64748b' : '#4ade80',
                      fontWeight: 700, fontSize: 15,
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {m.value}{m.unit ? ` ${m.unit}` : ''}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', color: '#94a3b8', fontSize: 12 }}>{m.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Comparison table (if available) */}
        {variant.comparison && (
          <div style={{ background: CARD_BG, borderRadius: 12, padding: 24, marginBottom: 20, border: '1px solid #1e293b' }}>
            <h3 style={{ color: '#e2e8f0', fontSize: '1rem', fontWeight: 600, margin: '0 0 16px 0' }}>
              Model Comparison
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #1e293b' }}>
                    <th style={{ textAlign: 'left', padding: '8px 10px', color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>Model</th>
                    <th style={{ textAlign: 'right', padding: '8px 10px', color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>S.U.N.</th>
                    <th style={{ textAlign: 'right', padding: '8px 10px', color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>Stable</th>
                    <th style={{ textAlign: 'right', padding: '8px 10px', color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>Novel</th>
                    <th style={{ textAlign: 'right', padding: '8px 10px', color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>Unique</th>
                    <th style={{ textAlign: 'right', padding: '8px 10px', color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>RMSD (Å)</th>
                    <th style={{ textAlign: 'right', padding: '8px 10px', color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>E_hull (eV)</th>
                  </tr>
                </thead>
                <tbody>
                  {variant.comparison.map((row, i) => {
                    const isHighlight = row.model.includes('MatterGen') && row.model.includes('Alex');
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #0f1923', background: isHighlight ? '#0a1a2e' : 'transparent' }}>
                        <td style={{ padding: '8px 10px', color: isHighlight ? '#38bdf8' : '#e2e8f0', fontSize: 13, fontWeight: isHighlight ? 700 : 400 }}>{row.model}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', color: '#4ade80', fontSize: 13, fontWeight: 600 }}>{row.sun}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', color: '#cbd5e1', fontSize: 13 }}>{row.stable}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', color: '#cbd5e1', fontSize: 13 }}>{row.novel}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', color: '#cbd5e1', fontSize: 13 }}>{row.unique}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', color: '#cbd5e1', fontSize: 13 }}>{row.rmsd}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', color: '#cbd5e1', fontSize: 13 }}>{row.eHull}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: '#64748b' }}>
              S.U.N. = Stable + Unique + Novel. Stability threshold: E_above_hull {'<'} 0.1 eV/atom (MP2020 convex hull).
              Source: MatterGen GitHub benchmark JSONs.
            </div>
          </div>
        )}

        {/* Test set & notes */}
        <div style={{ background: CARD_BG, borderRadius: 12, padding: 24, marginBottom: 20, border: '1px solid #1e293b' }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Test Set</div>
            <div style={{ color: '#e2e8f0', fontSize: 14 }}>{variant.testSet}</div>
          </div>
          {variant.notes && (
            <div>
              <div style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Notes</div>
              <div style={{ color: '#cbd5e1', fontSize: 13, lineHeight: 1.6 }}>{variant.notes}</div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
          <Link to="/docs/benchmarks" style={{ color: '#38bdf8', fontSize: 13, textDecoration: 'none' }}>← All Benchmarks</Link>
          <button
            onClick={() => navigate(-1)}
            style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 13, cursor: 'pointer' }}>
            Back
          </button>
        </div>
    </div>
  );
}
